/**
 * Confirm Dialog Host
 *
 * Renders whichever confirm request is at the front of the queue. Mounted once
 * next to <App />, so any screen can call confirmDialog() without wiring state
 * of its own.
 *
 * Built on the ui/Modal primitive, which brings Escape handling, focus trapping
 * and background scroll lock — this file used to hand-roll the first and do
 * neither of the others. It renders on the `confirm` layer so it stacks above an
 * ordinary modal, which is where most confirmations are triggered from.
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirmDialogStore } from './confirm-dialog-store';
import { Modal } from '../modal';
import { Button } from '../button';
import styles from './confirm-dialog.module.css';

export function ConfirmDialogHost() {
  const { t } = useTranslation();
  const current = useConfirmDialogStore((state) => state.queue[0]);
  const settleCurrent = useConfirmDialogStore((state) => state.settleCurrent);
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Cancel is focused rather than confirm: most of these dialogs guard a
  // destructive action, so a stray Enter should not delete anything. This runs
  // after Modal's own focus-first-element pass, so it wins.
  useEffect(() => {
    if (current) cancelRef.current?.focus();
  }, [current]);

  if (!current) return null;

  return (
    <Modal
      isOpen
      onClose={() => settleCurrent(false)}
      layer="confirm"
      size="sm"
      hideClose
      title={current.title}
      footer={
        <>
          <Button ref={cancelRef} variant="ghost" onClick={() => settleCurrent(false)}>
            {current.cancelLabel ?? t('common.cancel')}
          </Button>
          <Button
            variant={current.tone === 'danger' ? 'danger' : 'primary'}
            onClick={() => settleCurrent(true)}
          >
            {current.confirmLabel ?? t('common.confirm')}
          </Button>
        </>
      }
    >
      <p className={styles.message}>{current.message}</p>
    </Modal>
  );
}
